import {Component, Input, OnChanges} from '@angular/core';
import {LineupSpot} from "../../model/broadcast/lineup-spot";
import {Player, Record} from "../../model/broadcast/player";
import {UtilService} from "../../services/util.service";
import {Broadcast} from "../../model/broadcast/broadcast";
import {Team} from "../../model/broadcast/team";
import {NgClass, NgForOf, NgIf} from "@angular/common";
import {PitchStint} from "../../model/broadcast/pitch-stint";
import {ChronoCell, mapPitchStintsToCells, PitchCount, pitcherChangeCells} from "../pitch-counts";
import {PitchMark, pitchMarks, pitchMarksTitle} from "../pitch-marks";
import {playOutcome, PlayOutcome} from "../play-outcome";
import {battingOrderBySeq, Diamond, diamondFor} from "../diamond";

interface PlateAppearance {
  inning: number;
  play: string;
  outcome: PlayOutcome;
  playerGuid?: string;
  /** One mark per pitch of this at-bat, in order. Empty on older game docs. */
  pitches: PitchMark[];
  /**
   * What became of him on the bases, or null when there is nothing to draw —
   * an out with no base running, or a game parsed before the server wrote it.
   */
  diamond: Diamond | null;
}

interface InningSlot {
  inning: number;
  count: number;
}

interface PlayerEntry {
  number: string;
  name: string;
  position: string;
  isCurrentBatter: boolean;
}

interface SpotRow {
  players: PlayerEntry[];
  cells: (PlateAppearance | null)[];
  cellSubBoundary: boolean[];
  cellPitcherBoundary: boolean[];
  cellPitchCount: (PitchCount | null)[];
  isCurrentBatter: boolean;
}

interface CompressedCell {
  pa: PlateAppearance;
  inningStart?: number;
  inningEnd: boolean;
  subBoundary: boolean;
  pitcherBoundary: boolean;
  pitchCount?: PitchCount;
  key: string;
}

interface CompressedRow {
  players: PlayerEntry[];
  cells: CompressedCell[];
  isCurrentBatter: boolean;
}

interface UpcomingBatter {
  player: Player;
  number: string;
  position: string;
  isCurrentBatter: boolean;
}

interface BenchEntry {
  number: string;
  name: string;
}

@Component({
  selector: 'app-offense',
  templateUrl: './offense.component.html',
  styleUrls: ['./offense.component.scss'],
  standalone: true,
  imports: [NgIf, NgForOf, NgClass],
})
export class OffenseComponent implements OnChanges {

  @Input() lineup: LineupSpot[];
  @Input() broadcast: Broadcast;
  @Input() currentBatter?: Player;
  @Input() opposingPitchers?: Player[];
  @Input() opposingPitchStints?: PitchStint[];
  /** Inning the opposing pitcher is still working on — its count is not final. */
  @Input() openStintInning?: number;
  @Input() teamName?: string;
  @Input() team?: Team;
  @Input() showBench = false;
  /** Draw the diamond in each cell: the corners reached, and on whose account. */
  @Input() showBaseRunning = false;

  inningSlots: InningSlot[] = [];
  /** Which batting-order slot each plate appearance belongs to, for the corners. */
  private orderBySeq = new Map<number, number>();
  rows: SpotRow[] = [];
  compressedRows: CompressedRow[] = [];
  compressedColumnCount = 0;
  upcomingBatters: UpcomingBatter[] = [];
  bench: BenchEntry[] = [];
  isTeamAtBat = false;

  constructor(public utilService: UtilService) {
  }

  ngOnChanges(): void {
    this.rebuild();
  }

  pitchTitle(marks: PitchMark[]): string {
    return pitchMarksTitle(marks);
  }

  private rebuild(): void {
    if (!this.lineup?.length) {
      this.inningSlots = [];
      this.rows = [];
      this.compressedRows = [];
      this.compressedColumnCount = 0;
      this.upcomingBatters = [];
      this.bench = [];
      this.isTeamAtBat = false;
      return;
    }

    // Built before the cells, because a corner is labelled with the number of
    // the batter who moved the runner there — and that batter is always one of
    // this team's own.
    this.orderBySeq = battingOrderBySeq(this.lineup);

    const rawByInning: PlateAppearance[][][] = this.lineup.map(spot => this.collectSpot(spot));
    const inningCount = Math.max(9, ...rawByInning.flatMap(byInning => [byInning.length]));
    const paCountByInning = this.computePaCounts(rawByInning, inningCount);

    this.inningSlots = paCountByInning.map((count, idx) => ({inning: idx + 1, count}));

    const pitcherBoundaries = this.computePitcherBoundaries(rawByInning, paCountByInning);
    const pitchCounts = mapPitchStintsToCells(
      this.opposingPitchStints,
      this.buildChrono(rawByInning, paCountByInning) as ChronoCell[],
      this.openStintInning);

    this.rows = this.lineup.map((spot, spotIdx) =>
      this.buildRow(spot, rawByInning[spotIdx], paCountByInning, spotIdx, pitcherBoundaries, pitchCounts));

    this.compressedRows = this.lineup.map((spot, spotIdx) =>
      this.buildCompressedRow(spot, rawByInning[spotIdx], paCountByInning, spotIdx, pitcherBoundaries, pitchCounts));
    this.markInningTransitions(rawByInning, paCountByInning, this.compressedRows);
    this.compressedColumnCount = this.compressedRows.reduce((m, r) => Math.max(m, r.cells.length), 0);

    this.upcomingBatters = this.buildUpcomingBatters();
    this.bench = this.buildBench();
  }

  private buildCompressedRow(
    spot: LineupSpot,
    byInning: PlateAppearance[][],
    paCounts: number[],
    spotIdx: number,
    pitcherBoundaries: Set<string>,
    pitchCounts: Map<string, PitchCount>,
  ): CompressedRow {
    const cells: CompressedCell[] = [];
    let lastGuid: string | undefined;
    paCounts.forEach((count, inningIdx) => {
      const list = byInning[inningIdx] ?? [];
      for (let subSlot = 0; subSlot < count; subSlot++) {
        const pa = list[subSlot];
        if (!pa) continue;
        const subBoundary = !!(lastGuid && pa.playerGuid && pa.playerGuid !== lastGuid);
        const key = `${spotIdx}-${inningIdx}-${subSlot}`;
        cells.push({
          pa,
          inningStart: undefined,
          inningEnd: false,
          subBoundary,
          pitcherBoundary: pitcherBoundaries.has(key),
          pitchCount: pitchCounts.get(key),
          key,
        });
        if (pa.playerGuid) lastGuid = pa.playerGuid;
      }
    });

    const players: PlayerEntry[] = (spot.players ?? []).map(player => ({
      number: this.utilService.getNumber(player, this.broadcast),
      name: player?.name ?? '',
      position: this.utilService.positionToString(player?.position) ?? '',
      isCurrentBatter: !!this.currentBatter?.guid && player.guid === this.currentBatter.guid,
    }));
    while (players.length < 3) {
      players.push({number: '', name: '', position: '', isCurrentBatter: false});
    }

    return {
      players,
      cells,
      isCurrentBatter: players.some(p => p.isCurrentBatter),
    };
  }

  private buildBench(): BenchEntry[] {
    const teamGuid = this.team?.guid;
    const rosters = this.broadcast?.fullRosters ?? [];
    if (!teamGuid || !rosters.length) return [];

    const usedGuids = new Set<string>();
    this.lineup?.forEach(spot => spot.players?.forEach(p => p?.guid && usedGuids.add(p.guid)));
    this.team?.pitchers?.forEach(p => p?.guid && usedGuids.add(p.guid));

    return rosters
      .filter(p => p.teamGuid === teamGuid && p.guid && !usedGuids.has(p.guid))
      .map(p => ({
        number: this.utilService.getNumber(p, this.broadcast),
        name: p.name ?? '',
      }))
      .sort((a, b) => {
        const na = parseInt(a.number.replace('#', ''), 10);
        const nb = parseInt(b.number.replace('#', ''), 10);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        return a.name.localeCompare(b.name);
      });
  }

  private buildUpcomingBatters(): UpcomingBatter[] {
    const len = this.lineup.length;
    if (!len) return [];

    const atBatSpotIdx = this.findAtBatSpotIdx();
    this.isTeamAtBat = atBatSpotIdx >= 0;

    const startIdx = this.isTeamAtBat
      ? atBatSpotIdx
      : this.totalRecorded() % len;

    const cards: UpcomingBatter[] = [];
    for (let i = 0; i < 3; i++) {
      const spot = this.lineup[(startIdx + i) % len];
      const player = spot.players?.[spot.players.length - 1];
      if (!player) continue;
      cards.push({
        player,
        number: this.utilService.getNumber(player, this.broadcast),
        position: this.utilService.positionToString(player.position) ?? '',
        isCurrentBatter: i === 0 && this.isTeamAtBat,
      });
    }
    return cards;
  }

  private findAtBatSpotIdx(): number {
    const guid = this.currentBatter?.guid;
    if (!guid) return -1;
    return this.lineup.findIndex(spot => spot.players?.some(p => p.guid === guid));
  }

  private totalRecorded(): number {
    return this.lineup.reduce((sum, spot) =>
      sum + (spot.players?.reduce((s, p) => s + (p.records?.length ?? 0), 0) ?? 0), 0);
  }

  private collectSpot(spot: LineupSpot): PlateAppearance[][] {
    const byInning: PlateAppearance[][] = [];
    spot.players.forEach(player => {
      player.records?.forEach(record => {
        const idx = record.inning - 1;
        if (idx < 0) return;
        while (byInning.length <= idx) byInning.push([]);
        byInning[idx].push(this.parseRecord(record, player.guid));
      });
    });
    return byInning;
  }

  private computePaCounts(rawByInning: PlateAppearance[][][], inningCount: number): number[] {
    const counts: number[] = new Array(inningCount).fill(1);
    rawByInning.forEach(byInning => {
      byInning.forEach((paList, idx) => {
        if (paList.length > counts[idx]) counts[idx] = paList.length;
      });
    });
    return counts;
  }

  /**
   * Returns the set of "spotIdx-inningIdx-subSlot" keys that mark the LAST PA
   * faced by a pitcher before he was substituted. Walks PAs in TRUE
   * chronological order using cyclic batting order (same as inning markers)
   * so cumulative bfp counts match the actual game flow even when innings
   * end mid-lineup.
   */
  private computePitcherBoundaries(
    rawByInning: PlateAppearance[][][],
    paCounts: number[],
  ): Set<string> {
    const boundaries = new Set<string>();
    const pitchers = this.opposingPitchers ?? [];
    if (pitchers.length < 2) return boundaries;

    const chrono = this.buildChrono(rawByInning, paCounts);

    // The stints say which batter each pitcher finished on. Nothing below can
    // beat that, so the estimate is only for a document written before the
    // server sent them.
    const fromStints = pitcherChangeCells(this.opposingPitchStints, chrono as ChronoCell[]);
    if (fromStints.size) return fromStints;

    const bfps = pitchers.map(p => this.estimateBattersFaced(p));
    if (bfps.every(b => b === 0)) return boundaries;

    let cum = 0;
    // Mark the boundary AFTER each pitcher except the last (no change after him).
    for (let p = 0; p < bfps.length - 1; p++) {
      cum += bfps[p];
      if (cum >= 1 && cum <= chrono.length) {
        const last = chrono[cum - 1];
        boundaries.add(`${last.spotIdx}-${last.inningIdx}-${last.subSlot}`);
      }
    }
    return boundaries;
  }

  /**
   * Builds the team's chronological PA list using cyclic batting order.
   * Inning N starts at the spot after the one that batted last in inning N-1
   * (spot 0 for inning 1). Within an inning, subSlot 0 walks all spots
   * cyclically; subSlot s>0 (bat-around) continues from after the previous
   * PA.
   */
  private buildChrono(
    rawByInning: PlateAppearance[][][],
    paCounts: number[],
  ): { spotIdx: number; inningIdx: number; subSlot: number }[] {
    const chrono: { spotIdx: number; inningIdx: number; subSlot: number }[] = [];
    const numSpots = rawByInning.length;
    if (!numSpots) return chrono;

    let nextStart = 0;
    for (let inningIdx = 0; inningIdx < paCounts.length; inningIdx++) {
      const subCount = paCounts[inningIdx];
      let lastSpot = -1;
      for (let subSlot = 0; subSlot < subCount; subSlot++) {
        const cycleStart = subSlot === 0
          ? nextStart
          : (lastSpot >= 0 ? (lastSpot + 1) % numSpots : nextStart);
        for (let i = 0; i < numSpots; i++) {
          const spotIdx = (cycleStart + i) % numSpots;
          const pa = rawByInning[spotIdx][inningIdx]?.[subSlot];
          if (pa) {
            chrono.push({spotIdx, inningIdx, subSlot});
            lastSpot = spotIdx;
          }
        }
      }
      if (lastSpot >= 0) nextStart = (lastSpot + 1) % numSpots;
    }
    return chrono;
  }

  /**
   * Walks PAs in TRUE chronological order using the cyclic batting order:
   * inning N starts at the spot immediately after the spot that batted last
   * in inning N-1 (or spot 0 for inning 1). Within an inning we visit spots
   * in cyclic order from the starting spot; for bat-around (subSlot > 0) we
   * keep going from the spot after the previous PA. Each inning gets:
   *  - one badge on its first chronological PA (top-left inning number)
   *  - one double-line bottom border on its last chronological PA before
   *    the next inning starts.
   * "pozor na stridani" — when a spot didn't bat in some inning, the cycle
   * skips that spot and continues, but the inning-end is determined by the
   * actual last spot that batted, not by spot order.
   */
  private markInningTransitions(
    rawByInning: PlateAppearance[][][],
    paCounts: number[],
    rows: CompressedRow[],
  ): void {
    const cellByKey = new Map<string, CompressedCell>();
    rows.forEach(r => r.cells.forEach(c => cellByKey.set(c.key, c)));

    const chrono = this.buildChrono(rawByInning, paCounts)
      .map(e => cellByKey.get(`${e.spotIdx}-${e.inningIdx}-${e.subSlot}`)!)
      .filter(Boolean);

    const seen = new Set<number>();
    chrono.forEach(cell => {
      if (!seen.has(cell.pa.inning)) {
        cell.inningStart = cell.pa.inning;
        seen.add(cell.pa.inning);
      }
    });

    for (let i = 0; i < chrono.length - 1; i++) {
      if (chrono[i].pa.inning !== chrono[i + 1].pa.inning) {
        chrono[i].inningEnd = true;
      }
    }
  }

  /**
   * Approximates the number of batters faced by a pitcher. Prefers the bfp
   * stat, falls back to outs (derived from IP) + hits + walks + HBP. WBSC
   * data often omits bfp on finished games, so the fallback is what actually
   * draws the boundary lines.
   */
  private estimateBattersFaced(p: Player): number {
    const ps = p.gameStats?.pitching;
    if (!ps) return 0;
    if ((ps.bfp ?? 0) > 0) return ps.bfp!;
    return this.ipToOuts(ps.ip) + (ps.h ?? 0) + (ps.bb ?? 0) + (ps.hbp ?? 0);
  }

  private ipToOuts(ip?: number): number {
    if (!ip || ip <= 0) return 0;
    const whole = Math.floor(ip);
    const frac = Math.round((ip - whole) * 10);
    return whole * 3 + frac;
  }

  private buildRow(
    spot: LineupSpot,
    byInning: PlateAppearance[][],
    paCounts: number[],
    spotIdx: number,
    pitcherBoundaries: Set<string>,
    pitchCounts: Map<string, PitchCount>,
  ): SpotRow {
    const cells: (PlateAppearance | null)[] = [];
    const cellPitcherBoundary: boolean[] = [];
    const cellPitchCount: (PitchCount | null)[] = [];
    paCounts.forEach((count, inningIdx) => {
      const list = byInning[inningIdx] ?? [];
      for (let subSlot = 0; subSlot < count; subSlot++) {
        const key = `${spotIdx}-${inningIdx}-${subSlot}`;
        cells.push(list[subSlot] ?? null);
        cellPitcherBoundary.push(pitcherBoundaries.has(key));
        cellPitchCount.push(pitchCounts.get(key) ?? null);
      }
    });

    const cellSubBoundary: boolean[] = cells.map(() => false);
    let lastGuid: string | undefined;
    cells.forEach((pa, idx) => {
      if (!pa?.playerGuid) return;
      if (lastGuid && pa.playerGuid !== lastGuid) {
        cellSubBoundary[idx] = true;
      }
      lastGuid = pa.playerGuid;
    });

    const players: PlayerEntry[] = (spot.players ?? []).map(player => ({
      number: this.utilService.getNumber(player, this.broadcast),
      name: player?.name ?? '',
      position: this.utilService.positionToString(player?.position) ?? '',
      isCurrentBatter: !!this.currentBatter?.guid && player.guid === this.currentBatter.guid,
    }));
    // Pre-print empty slots so each spot always has 3 lines (starter + 2 subs),
    // mirroring a classic paper scoresheet. A 4th sub will let the row grow.
    while (players.length < 3) {
      players.push({number: '', name: '', position: '', isCurrentBatter: false});
    }

    return {
      players,
      cells,
      cellSubBoundary,
      cellPitcherBoundary,
      cellPitchCount,
      isCurrentBatter: players.some(p => p.isCurrentBatter),
    };
  }

  private parseRecord(record: Record, playerGuid?: string): PlateAppearance {
    const play = (record.play ?? '').trim();
    const outcome = playOutcome(play);
    return {
      inning: record.inning,
      play: outcome === 'unknown' ? '?' : play,
      outcome,
      playerGuid,
      pitches: pitchMarks(record.pitches),
      diamond: diamondFor(record, this.orderBySeq),
    };
  }

}
